import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from "reactstrap";
import classnames from "classnames";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

import { PersonalDetails } from "./PersonalDetails";
import { ChangePassword } from "./ChangePassword";
import { ProfilePicture } from "./ProfilePicture";
import { TeacherScheduleSettings } from "./TeacherScheduleSettings";
import { RoleEnum } from "../../enums";
import { AccountState } from "../../store/slices/accountSlice";
import {
  markTeacherScheduleHighlightSeen,
  shouldShowTeacherScheduleHighlight,
  TEACHER_SCHEDULE_HIGHLIGHT_EVENT,
} from "../../utils/teacherScheduleHighlight";

const TEACHER_SCHEDULE_TAB = "teacher-schedule";

export const SettingsTabPage = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();

  const profile = useSelector(
    (state: { account: AccountState }) => state.account.userProfile,
  );

  const isTeacherAdmin = profile?.role?.key === RoleEnum.TEACHER_ADMIN;

  const initialTab = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");
    if (tab === TEACHER_SCHEDULE_TAB && isTeacherAdmin) {
      return TEACHER_SCHEDULE_TAB;
    }
    return "1";
  }, [location.search, isTeacherAdmin]);

  const [tab, setTab] = useState(initialTab);
  const [showScheduleBadge, setShowScheduleBadge] = useState(
    shouldShowTeacherScheduleHighlight(profile?.role?.key),
  );

  document.title = `${t("documentTitle", { pageName: "settings" })}`;

  useEffect(() => {
    setTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    const onHighlightSeen = () => setShowScheduleBadge(false);
    window.addEventListener(TEACHER_SCHEDULE_HIGHLIGHT_EVENT, onHighlightSeen);
    return () =>
      window.removeEventListener(
        TEACHER_SCHEDULE_HIGHLIGHT_EVENT,
        onHighlightSeen,
      );
  }, []);

  const toggleTab = (changingTab: string) => {
    if (changingTab === tab) return;

    setTab(changingTab);
    if (changingTab === TEACHER_SCHEDULE_TAB) {
      history.replace(`/settings?tab=${TEACHER_SCHEDULE_TAB}`);
      markTeacherScheduleHighlightSeen();
      setShowScheduleBadge(false);
      return;
    }

    history.replace("/settings");
  };

  const onTeacherScheduleVisited = useCallback(() => {
    if (tab === TEACHER_SCHEDULE_TAB) {
      markTeacherScheduleHighlightSeen();
      setShowScheduleBadge(false);
    }
  }, [tab]);

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col xxl={3}>
            <ProfilePicture />
          </Col>

          <Col xxl={9}>
            <Card>
              <CardHeader>
                <Nav
                  className="nav-tabs-custom rounded card-header-tabs border-bottom-0"
                  role="tablist"
                >
                  <NavItem>
                    <NavLink
                      className={classnames({ active: tab === "1" })}
                      onClick={() => toggleTab("1")}
                    >
                      <i className="fas fa-home"></i>
                      {t("personalDetails")}
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={classnames({ active: tab === "2" })}
                      onClick={() => toggleTab("2")}
                    >
                      <i className="far fa-user"></i>
                      {t("changePassword")}
                    </NavLink>
                  </NavItem>
                  {isTeacherAdmin && (
                    <NavItem>
                      <NavLink
                        className={classnames({
                          active: tab === TEACHER_SCHEDULE_TAB,
                        })}
                        onClick={() => toggleTab(TEACHER_SCHEDULE_TAB)}
                      >
                        <i className="ri-calendar-schedule-line"></i>
                        {t("teacherSchedule")}
                        {showScheduleBadge && (
                          <span className="badge bg-success ms-2">
                            {t("new")}
                          </span>
                        )}
                      </NavLink>
                    </NavItem>
                  )}
                </Nav>
              </CardHeader>
              <CardBody className="p-4">
                <TabContent activeTab={tab}>
                  {tab === "1" && (
                    <TabPane tabId="1">
                      <PersonalDetails />
                    </TabPane>
                  )}
                  {tab === "2" && (
                    <TabPane tabId="2">
                      <ChangePassword />
                    </TabPane>
                  )}
                  {isTeacherAdmin && tab === TEACHER_SCHEDULE_TAB && (
                    <TabPane tabId={TEACHER_SCHEDULE_TAB}>
                      <TeacherScheduleSettings
                        showNewFeatureAlert
                        onVisited={onTeacherScheduleVisited}
                      />
                    </TabPane>
                  )}
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};
